import { fetchClimateOptions, useRoiCalculation } from "@/api/api";
import { Header } from "@/components/header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { DollarSign, TrendingUp, PiggyBank, Send } from "lucide-react";
import React, { useEffect, useState, useRef } from "react";
import Confetti from "react-confetti";
import useWindowSize from 'react-use/lib/useWindowSize';

const RoiCalculator: React.FC = () => {
    // User Information State Variables
    const [userInfo, setUserInfo] = useState({
        buildingAddress: "",
        name: "",
        role: "",
        company: "",
        email: "",
        phoneNumber: "",
    });

    // ROI Calculation State Variables
    const [roiInputs, setRoiInputs] = useState({
        refrigerantCooling: false,
        refrigerantHeating: false,
        squareFootage: "",
        annualEnergyConsumption: "",
        climateZone: "",
        climateRegion: "",
        numberOfCoolingUnits: "",
        numberOfHeatingUnits: "",
        primaryHeatingMethod: "Electricity", // Default value
        years: 5,
        perUnitRefrigerantInstallationCost: 1000,
        baseAnnualSubscriptionCost: 0,
        annualOnSiteRepairCostPerUnit: 0,
    });

    const [climateOptions, setClimateOptions] = useState<{
        climate_regions: string[];
        climate_zones: string[];
    }>({ climate_regions: [], climate_zones: [] });
    const [showConfetti, setShowConfetti] = useState(false);
    const { width, height } = useWindowSize();
    const [loading, setLoading] = useState(true);
    const [showOptionalFields, setShowOptionalFields] = useState(false);
    const [hasSubmittedOptional, setHasSubmittedOptional] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showRoiNumbers, setShowRoiNumbers] = useState(false); // New state to control ROI display

    // Reference for the results section
    const buildingResultRef = useRef<HTMLDivElement | null>(null);
    const resultsRef = useRef<HTMLDivElement | null>(null);

    // Prepare parameters for ROI calculation
    const roiParams = React.useMemo(() => {
        const params: {
            squareFootage: number;
            annualEnergyConsumption: number;
            climateZone: string;
            climateRegion: string;
            numberOfCoolingUnits: number;
            numberOfHeatingUnits: number;
            primaryHeatingMethod: string;
            years: number;
            perUnitRefrigerantInstallationCost: number;
            baseAnnualSubscriptionCost: number;
            annualOnSiteRepairCostPerUnit: number;
            buildingAddress: string;
            name?: string;
            role?: string;
            company?: string;
            email?: string;
            phoneNumber?: string;
        } = {
            squareFootage: Number(roiInputs.squareFootage),
            annualEnergyConsumption: Number(roiInputs.annualEnergyConsumption),
            climateZone: roiInputs.climateZone,
            climateRegion: roiInputs.climateRegion,
            numberOfCoolingUnits: Number(roiInputs.numberOfCoolingUnits),
            numberOfHeatingUnits: Number(roiInputs.numberOfHeatingUnits),
            primaryHeatingMethod: roiInputs.primaryHeatingMethod,
            years: roiInputs.years,
            perUnitRefrigerantInstallationCost:
                roiInputs.perUnitRefrigerantInstallationCost,
            baseAnnualSubscriptionCost: roiInputs.baseAnnualSubscriptionCost,
            annualOnSiteRepairCostPerUnit: roiInputs.annualOnSiteRepairCostPerUnit,
            buildingAddress: userInfo.buildingAddress,
        };

        // Include optional fields only if they have been submitted
        if (hasSubmittedOptional) {
            params["name"] = userInfo.name;
            params["role"] = userInfo.role;
            params["company"] = userInfo.company;
            params["email"] = userInfo.email;
            params["phoneNumber"] = userInfo.phoneNumber;
        }

        return params;
    }, [
        roiInputs.squareFootage,
        roiInputs.annualEnergyConsumption,
        roiInputs.climateZone,
        roiInputs.climateRegion,
        roiInputs.numberOfCoolingUnits,
        roiInputs.numberOfHeatingUnits,
        roiInputs.primaryHeatingMethod,
        roiInputs.years,
        roiInputs.perUnitRefrigerantInstallationCost,
        roiInputs.baseAnnualSubscriptionCost,
        roiInputs.annualOnSiteRepairCostPerUnit,
        userInfo.buildingAddress,
        hasSubmittedOptional,
        // Include optional fields in dependencies when hasSubmittedOptional is true
        hasSubmittedOptional ? userInfo.name : null,
        hasSubmittedOptional ? userInfo.role : null,
        hasSubmittedOptional ? userInfo.company : null,
        hasSubmittedOptional ? userInfo.email : null,
        hasSubmittedOptional ? userInfo.phoneNumber : null,
    ]);

    const {
        data: roiResult,
        refetch,
        isFetching: isRefetching,
    } = useRoiCalculation(roiParams);

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhoneNumber = (phoneNumber: string) => {
        const phoneRegex = /^\+?(\d{1,3})?[-.\s]?(\(?\d{1,4}\)?)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/;
        return phoneRegex.test(phoneNumber);
    };

    const validateAddress = (address: string) => {
        return address.trim().length > 5;
    };

// **Function to fill the form with example data**
    const fillWithExampleData = () => {
        setUserInfo({
            buildingAddress: "141 Flushing Ave",
            name: "Nomen Nescio",
            role: "Building Manager",
            company: "Thalo Labs",
            email: "hello@thalolabs.com",
            phoneNumber: "+1 917-274-7723",
        });

        setRoiInputs({
            refrigerantCooling: true,
            refrigerantHeating: false,
            squareFootage: "100000",
            annualEnergyConsumption: "500000",
            climateZone: climateOptions.climate_zones[1] || "",
            climateRegion: climateOptions.climate_regions[0] || "",
            numberOfCoolingUnits: "10",
            numberOfHeatingUnits: "5",
            primaryHeatingMethod: "Electricity",
            years: 5,
            perUnitRefrigerantInstallationCost: 1000,
            baseAnnualSubscriptionCost: 0,
            annualOnSiteRepairCostPerUnit: 0,
        });
    };




    // Handle the initial ROI calculation
    const handleCalculate = async () => {
        if (!validateAddress(userInfo.buildingAddress)) {
            alert("Please enter a valid building address.");
            return;
        }

        try {
            const result = await refetch();

            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 10000);

            // Check if potential savings is greater than 0 using the latest data
            if (result.data && result.data.cumulative_savings > 0) {
                setShowOptionalFields(true);
                // Scroll to the results section after submission
                setTimeout(() => {
                    if (buildingResultRef.current) {
                        buildingResultRef.current.scrollIntoView({ behavior: "smooth" });
                    }
                }, 100);
            } else {
                alert("No potential savings found based on the provided information.");
            }
        } catch (error) {
            console.error("Error fetching ROI data:", error);
            alert("An error occurred while calculating Savings. Please try again.");
        }
    };

    // Handle submitting the optional contact information
    const handleOptionalSubmit = async () => {
        if (!validateEmail(userInfo.email)) {
            alert("Please enter a valid email address.");
            return;
        }
        if (!validatePhoneNumber(userInfo.phoneNumber)) {
            alert("Please enter a valid phone number.");
            return;
        }

        // Update state to include optional info
        setHasSubmittedOptional(true);
        setShowOptionalFields(false);
        setShowSuccessMessage(false);
        setShowRoiNumbers(false);
    };

    // Effect to handle refetching when optional info is submitted
    useEffect(() => {
        const submitOptionalInfo = async () => {
            if (hasSubmittedOptional) {
                try {
                    const result = await refetch();

                    setShowConfetti(true);
                    setTimeout(() => setShowConfetti(false), 10000);

                    // Show ROI numbers and success message after successful refetch
                    if (result.data) {
                        setShowSuccessMessage(true);
                        setShowRoiNumbers(true);

                        // Scroll to the results section after submission
                        setTimeout(() => {
                            if (resultsRef.current) {
                                resultsRef.current.scrollIntoView({ behavior: "smooth" });
                            }
                        }, 100);
                    }
                } catch (error) {
                    console.error("Error submitting optional info:", error);
                    alert("An error occurred while submitting your information. Please try again.");
                }
            }
        };

        submitOptionalInfo();
    }, [hasSubmittedOptional, refetch]);

    // Fetch climate options on component mount
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const data = await fetchClimateOptions();
                setClimateOptions(data);

                if (data.climate_zones.length > 0) {
                    setRoiInputs((prev) => ({
                        ...prev,
                        climateZone: data.climate_zones[0],
                    }));
                }
                if (data.climate_regions.length > 0) {
                    setRoiInputs((prev) => ({
                        ...prev,
                        climateRegion: data.climate_regions[0],
                    }));
                }
                setLoading(false);
            } catch (error) {
                console.error("Error fetching climate options:", error);
                alert("An error occurred while fetching climate options. Please try again.");
                setLoading(false);
            }
        };
        fetchOptions();
    }, []);

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="container mx-auto p-4">

            {showConfetti && <Confetti width={width} height={height} />}

            <Header
                name=""
                showDatePicker={false}
                showChartOptions={false}
                className="mb-6"
            />

            <Card className="p-6">


                <div className="mb-4 flex justify-end">
                    <Button onClick={fillWithExampleData}>Fill with Example Data</Button>
                </div>

                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleCalculate();
                    }}
                >

                    {/* Required Fields Section */}
                    <section className="mb-8">
                        <h2 className="text-xl font-semibold mb-4">
                            Building Information
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Building Address <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    type="text"
                                    placeholder="Enter building address"
                                    value={userInfo.buildingAddress}
                                    onChange={(e) =>
                                        setUserInfo({
                                            ...userInfo,
                                            buildingAddress: e.target.value,
                                        })
                                    }
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Square Footage <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    type="number"
                                    placeholder="Enter square footage"
                                    value={roiInputs.squareFootage}
                                    onChange={(e) =>
                                        setRoiInputs({ ...roiInputs, squareFootage: e.target.value })
                                    }
                                    required
                                    min="0"
                                />
                            </div>
                            {/* Climate Zone */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Climate Zone <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    value={roiInputs.climateZone}
                                    onValueChange={(value) =>
                                        setRoiInputs({ ...roiInputs, climateZone: value })
                                    }
                                    required
                                >
                                    <SelectTrigger>
                                        <SelectValue
                                            placeholder="Select a climate zone"
                                            aria-required
                                        />
                                    </SelectTrigger>
                                        <SelectContent>
                                            {climateOptions.climate_zones.map((zone) => (
                                                <SelectItem key={zone} value={zone}>
                                                    {zone === "Cool" ? "Cool - New York" : zone}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>

                                </Select>
                            </div>
                            {/* Climate Region */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Climate Region <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    value={roiInputs.climateRegion}
                                    onValueChange={(value) =>
                                        setRoiInputs({ ...roiInputs, climateRegion: value })
                                    }
                                    required
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a climate region" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {climateOptions.climate_regions.map((region) => (
                                            <SelectItem key={region} value={region}>
                                                    {region === "Middle Atlantic" ? "Middle Atlantic - New York" : region}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {/* Number of Cooling Units */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Cooling Equipment Quantity <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    type="number"
                                    placeholder="Enter cooling equipment quantity"
                                    value={roiInputs.numberOfCoolingUnits}
                                    onChange={(e) =>
                                        setRoiInputs({ ...roiInputs, numberOfCoolingUnits: e.target.value })
                                    }
                                    required
                                    min="0"
                                />
                            </div>
                            {/* Number of Heating Units */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Cooling Heating Quantity <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    type="number"
                                    placeholder="Enter heating equipment quantity"
                                    value={roiInputs.numberOfHeatingUnits}
                                    onChange={(e) =>
                                        setRoiInputs({ ...roiInputs, numberOfHeatingUnits: e.target.value })
                                    }
                                    required
                                    min="0"
                                />
                            </div>
                            {/* Primary Heating Method */}
                            {Number(roiInputs.numberOfHeatingUnits) > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Primary Heating Method <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex items-center space-x-4">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="primaryHeatingMethod"
                                                value="Fuel"
                                                checked={roiInputs.primaryHeatingMethod === "Fuel"}
                                                onChange={(e) =>
                                                    setRoiInputs({ ...roiInputs, primaryHeatingMethod: e.target.value })
                                                }
                                                className="mr-2"
                                                required
                                            />
                                            Fuel
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="primaryHeatingMethod"
                                                value="Electricity"
                                                checked={roiInputs.primaryHeatingMethod === "Electricity"}
                                                onChange={(e) =>
                                                    setRoiInputs({ ...roiInputs, primaryHeatingMethod: e.target.value })
                                                }
                                                className="mr-2"
                                                required
                                            />
                                            Electricity
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    <Button type="submit" className="w-full">
                        Calculate Savings
                    </Button>

                    {/* Optional Information Section */}
                    {showOptionalFields && (
                        <div ref={buildingResultRef} className="mt-8">
                            <div className="p-4 bg-green-100 text-green-700 rounded">
                                <div className="flex items-center">
                                    <PiggyBank className="w-10 h-10 mr-2" />
                                    <p>
                                        Congratulations! Thalo Copilot can save you money. Provide some more information to get your INSTANT estimate.
                                    </p>
                                </div>
                            </div>
                            {/* Optional Fields Section */}
                            {!hasSubmittedOptional && (
                                <>
                                    <section className="mb-8 mt-4">
                                        <h2 className="text-xl font-semibold mb-4">
                                            Contact Information
                                        </h2>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Name
                                                </label>
                                                <Input
                                                    type="text"
                                                    placeholder="Enter your name"
                                                    value={userInfo.name}
                                                    onChange={(e) =>
                                                        setUserInfo({ ...userInfo, name: e.target.value })
                                                    }
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Role
                                                </label>
                                                <Input
                                                    type="text"
                                                    placeholder="Enter your role"
                                                    value={userInfo.role}
                                                    onChange={(e) =>
                                                        setUserInfo({ ...userInfo, role: e.target.value })
                                                    }
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Company
                                                </label>
                                                <Input
                                                    type="text"
                                                    placeholder="Enter your company"
                                                    value={userInfo.company}
                                                    onChange={(e) =>
                                                        setUserInfo({ ...userInfo, company: e.target.value })
                                                    }
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Email
                                                </label>
                                                <Input
                                                    type="email"
                                                    placeholder="Enter your email"
                                                    value={userInfo.email}
                                                    onChange={(e) =>
                                                        setUserInfo({ ...userInfo, email: e.target.value })
                                                    }
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Phone Number
                                                </label>
                                                <Input
                                                    type="tel"
                                                    placeholder="Enter your phone number"
                                                    value={userInfo.phoneNumber}
                                                    onChange={(e) =>
                                                        setUserInfo({
                                                            ...userInfo,
                                                            phoneNumber: e.target.value,
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </section>

                                    <Button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleOptionalSubmit();
                                        }}
                                        className="mt-6 w-full"
                                    >
                                        Submit Info
                                    </Button>
                                </>
                            )}
                        </div>
                    )}

                    {/* ROI Numbers Displayed After Submitting Optional Info */}
                    {showRoiNumbers && roiResult && (
                        <div ref={resultsRef} className="mt-8 congratulations-section">
                            <h2 className="text-xl font-semibold mb-4">
                                Your Instant Savings Estimate
                            </h2>
                            <div className="flex flex-col space-y-4">
                                <p className="text-lg flex items-center space-x-2 animate-bounce">
                                    <DollarSign className="text-green-500" />
                                    <span>
                                        Potential Savings ({roiInputs.years} years):{" "}
                                        <span className="font-bold text-green-600">
                                            ${roiResult.cumulative_savings.toFixed(0)}
                                        </span>
                                    </span>
                                </p>
                                {
                                    roiResult.cumulative_erp_tons > 0 && (
                                        <p className="text-lg flex items-center space-x-2 animate-pulse">
                                            <TrendingUp className="text-blue-500" />
                                            <span>
                                                Potential Tons of CO2e Avoided:{" "}
                                                <span className="font-bold text-blue-600">
                                                    {roiResult.cumulative_erp_tons.toFixed(0)} tons
                                                </span>
                                            </span>
                                        </p>)
                                }
                            </div>

                            {/* Success Message */}
                            {showSuccessMessage && (
                                <div className="mt-4 p-4 bg-blue-100 text-blue-700 rounded">
                                    <div className="flex items-center">
                                        <Send className="w-10 h-10 mr-2" />
                                        <p>
                                            Thank you! Your information has been successfully submitted.{" "}
                                            <span className="ml-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="flex items-center bg-white"
                                                    onClick={() =>
                                                        window.open(
                                                            "https://calendly.com/thalolabs-partnerships/meet-with-thalo-labs",
                                                            "_blank"
                                                        )
                                                    }>
                                                    Schedule a Demo
                                                </Button>
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            )}




                        </div>
                    )}

                    {/* Show loader if refetching */}
                    {isRefetching && (
                        <div className="mt-4">
                            <p>Updating your results...</p>
                        </div>
                    )}
                </form>

                {/* Assumptions Message (Under Success Message) */}
                <div className="mt-4 text-xs rounded}">
                    <h3 className="font-bold">Contact support@thalolabs.com for questions</h3>
                    <h3 className=" mt-4 font-semibold">Assumptions:</h3>
                    <ul className="list-disc list-inside mt-2 text-gray-700 ">
                        <li>Building energy consumption estimated from publicly available data or Energy Information Administration (EIA) models for similar buildings.</li>
                        <li>Electricity and fuel costs based on publicly available data for commercial rates.</li>
                        <li>Savings estimates based on data from existing Thalo installations on similar buildings.</li>
                        <li>Emissions factors provided by the EPA Greenhouse Gas Inventories.</li>
                        <li>Number of cooling and heating units does not affect savings because total building cooling and heating capacity is assumed to be fixed regardless of the number of units.</li>
                    </ul>
                    </div>
            </Card>
        </div>
    );
};

export default RoiCalculator;